// CTSK-351 (folds in CTSK-352) — URL-template interpolator for the
// registryFiles[].rowActions palette. Small, pure, framework-free so
// the shared package could adopt it later without a React dependency
// if a server-side action-URL preview endpoint ever ships.
//
// Contract:
//   - `{name}` placeholders resolve to `entry[name]` and are always
//     URL-encoded (via encodeURIComponent) so operator-authored
//     template strings can never smuggle a raw `#` or `&` through the
//     entry data into the target URL.
//   - Missing / null / undefined values resolve to '' — the caller's
//     `requiresField` gate is where "hide the action entirely" belongs;
//     the interpolator's job is to produce a well-formed URL even for
//     unusual data.
//   - Placeholder characters (`{` / `}`) that don't wrap a matched
//     identifier are left literal. Unmatched placeholders like `{x}`
//     when the entry lacks `x` resolve to '' (documented; consistent
//     with the missing-field rule).
//   - The `clipboard:` scheme prefix is a palette-level concern — this
//     helper doesn't special-case it; it just runs the substitution.
//     `clipboard:{url}` interpolates to `clipboard:https%3A%2F%2F…`,
//     which the palette recognizes by prefix and hands to
//     copyToClipboard (see RegistryRowActionsPalette.tsx). URL
//     encoding of the payload is safe because the palette decodes
//     before copying — the encode step is defense-in-depth against
//     newlines / control chars in poisonous entry data.

/** Interpolate `{name}` placeholders in `template` against `entry`,
 *  URL-encoding every substituted value. Non-string values coerce
 *  via `String(v)`. Unknown / missing / null / undefined values
 *  substitute to '' rather than throwing — the palette's
 *  `requiresField` filter is what prevents these from ever being
 *  rendered to the operator. */
export function interpolate(
  template: string,
  entry: Record<string, unknown>,
): string {
  // Identifier grammar matches ToolActionSchema.key + RegistryFileEntry.rowActions.slug:
  // lower/upper letters, digits, underscore. Broadened from just \w to allow
  // camelCase / snake_case entry field names without extra config.
  return template.replace(/\{([A-Za-z_][A-Za-z0-9_]*)\}/g, (_, key: string) => {
    const value = (entry as Record<string, unknown>)[key];
    if (value == null) return '';
    return encodeURIComponent(String(value));
  });
}

// ── CTSK-364 — extended interpolation for fetch-and-map actions ─────
//
// Two paths with DIFFERENT serialization rules:
//
//   interpolateUrl(template, entry, ctx)  — query-string semantics.
//     Every value URL-encoded (same guarantee as interpolate()); arrays
//     serialize comma-separated BEFORE encoding ({tags} → "a%2Cb").
//
//   interpolateBody(template, entry, ctx) — JSON semantics. A value
//     that is EXACTLY one placeholder ("{tags}") injects the native
//     value (arrays stay arrays, strings stay strings — no URL
//     encoding, no coercion); placeholders embedded in longer strings
//     ("scanned {title} today") substitute String(v) inline, raw.
//     Nested objects/arrays recurse. Missing fields: exact-placeholder
//     values inject null (JSON-honest "absent"); embedded ones
//     substitute ''.
//
// `ctx` supplies computed variables that aren't entry fields — the
// first tenant is {captain_tasko_ref}, the composite link back to the
// entry in CT (built by the caller, which knows the page route).
// ctx wins over entry on name collision (computed > raw is the least
// surprising precedence for manifest authors; entry fields can't
// shadow a declared context variable).
//
// XSS note: bodyTemplate values travel as JSON (never innerHTML), and
// urlTemplate keeps the encodeURIComponent guarantee — a poisonous
// entry field can't smuggle markup into either path.

const PLACEHOLDER = /\{([A-Za-z_][A-Za-z0-9_]*)\}/g;
const EXACT_PLACEHOLDER = /^\{([A-Za-z_][A-Za-z0-9_]*)\}$/;

/** Extra computed variables (e.g. captain_tasko_ref) for the CTSK-364
 *  interpolation paths. Values are injected natively by
 *  interpolateBody and String()+encoded by interpolateUrl. */
export type InterpolateContext = Record<string, unknown>;

function lookup(
  key: string,
  entry: Record<string, unknown>,
  ctx?: InterpolateContext,
): unknown {
  if (ctx && key in ctx) return ctx[key];
  return entry[key];
}

/** CTSK-364 — interpolate `{name}` placeholders with URL semantics:
 *  every substituted value URL-encoded; arrays comma-joined before
 *  encoding; missing/null values → ''. Superset of interpolate() —
 *  same output for scalar-only entries with no ctx. */
export function interpolateUrl(
  template: string,
  entry: Record<string, unknown>,
  ctx?: InterpolateContext,
): string {
  return template.replace(PLACEHOLDER, (_, key: string) => {
    const value = lookup(key, entry, ctx);
    if (value == null) return '';
    const flat = Array.isArray(value) ? value.map((v) => String(v)).join(',') : String(value);
    return encodeURIComponent(flat);
  });
}

function interpolateBodyValue(
  value: unknown,
  entry: Record<string, unknown>,
  ctx?: InterpolateContext,
): unknown {
  if (typeof value === 'string') {
    const exact = value.match(EXACT_PLACEHOLDER);
    if (exact) {
      const resolved = lookup(exact[1]!, entry, ctx);
      return resolved === undefined ? null : resolved;
    }
    return value.replace(PLACEHOLDER, (_, key: string) => {
      const v = lookup(key, entry, ctx);
      return v == null ? '' : String(v);
    });
  }
  if (Array.isArray(value)) {
    return value.map((v) => interpolateBodyValue(v, entry, ctx));
  }
  if (value !== null && typeof value === 'object') {
    const out: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
      out[k] = interpolateBodyValue(v, entry, ctx);
    }
    return out;
  }
  return value;
}

/** CTSK-364 — interpolate a JSON bodyTemplate against the entry with
 *  native-type semantics (see the block comment above). Returns a NEW
 *  object; the template is never mutated. */
export function interpolateBody(
  template: Record<string, unknown>,
  entry: Record<string, unknown>,
  ctx?: InterpolateContext,
): Record<string, unknown> {
  return interpolateBodyValue(template, entry, ctx) as Record<string, unknown>;
}

// ── CTSK-364 — responseMap helpers ──────────────────────────────────

/** Read a dotted path off a parsed JSON response. Missing segments /
 *  non-object intermediates return undefined (mapped keys are simply
 *  skipped by buildResponseMapPatch — a partial external response
 *  patches what it CAN rather than failing the whole capture). */
export function getByDottedPath(obj: unknown, path: string): unknown {
  let cur: unknown = obj;
  for (const seg of path.split('.')) {
    if (cur === null || typeof cur !== 'object') return undefined;
    cur = (cur as Record<string, unknown>)[seg];
  }
  return cur;
}

/** Build a nested entry patch from a responseMap + a parsed response.
 *  Each map entry reads `fromPath` off the response and writes it at
 *  `toPath` (dotted) in the patch object, e.g.
 *    { "links.uuid": "external_refs.hyper_scan.uuid" }
 *  → { external_refs: { hyper_scan: { uuid: <value> } } }.
 *  Response paths that resolve to undefined are skipped. Returns null
 *  when NOTHING resolved (callers can then skip the PATCH entirely
 *  rather than writing an empty patch). */
export function buildResponseMapPatch(
  responseMap: Record<string, string>,
  response: unknown,
): Record<string, unknown> | null {
  const patch: Record<string, unknown> = {};
  let wrote = false;
  for (const [fromPath, toPath] of Object.entries(responseMap)) {
    const value = getByDottedPath(response, fromPath);
    if (value === undefined) continue;
    const segs = toPath.split('.').filter((s) => s.length > 0);
    if (segs.length === 0) continue;
    let cursor = patch;
    for (let i = 0; i < segs.length - 1; i++) {
      const seg = segs[i]!;
      const next = cursor[seg];
      if (next === null || typeof next !== 'object' || Array.isArray(next)) {
        cursor[seg] = {};
      }
      cursor = cursor[seg] as Record<string, unknown>;
    }
    cursor[segs[segs.length - 1]!] = value;
    wrote = true;
  }
  return wrote ? patch : null;
}

/** Decode the payload of a `clipboard:` scheme URL produced by
 *  interpolate() above. Kept alongside the interpolator so the
 *  encode/decode pair has one obvious home. Non-clipboard URLs
 *  return null (the palette treats null as "not a clipboard action"). */
export function decodeClipboardPayload(url: string): string | null {
  if (!url.startsWith('clipboard:')) return null;
  const payload = url.slice('clipboard:'.length);
  try {
    return decodeURIComponent(payload);
  } catch {
    // Malformed percent-encoding — return the raw payload rather than
    // throwing at click time. Operator sees a toast in the palette.
    return payload;
  }
}
